<template>
  <div class="auth-layout-gate">
    <iframe
      v-if="waitForBrandPanel && !brandReady && preloadSrc"
      class="auth-layout__iframe-preload"
      :src="preloadSrc"
      :sandbox="AUTH_BRAND_PANEL_IFRAME_SANDBOX"
      title=""
      tabindex="-1"
      aria-hidden="true"
      data-testid="auth-layout__iframe-preload"
      @load="onPreloadIframeReady"
      @error="onPreloadIframeReady"
    />
    <div
      v-if="showLoadingScreen"
      class="auth-layout__loader"
      data-testid="auth-layout__loader"
    >
      <AppLoadingScreen />
    </div>
    <v-app
      v-if="layoutVisible"
      class="auth-layout"
      :data-testid="dataTestid"
    >
      <AuthBrandPanel :slides="slides" />
      <div class="auth-layout__panel">
        <div class="auth-layout__main">
          <v-card
            class="auth-layout__card"
            elevation="0"
            loader-height="2"
            :loading="loading"
            :disabled="disabled"
          >
            <v-card-text class="auth-layout__card-text">
              <slot />
            </v-card-text>
          </v-card>
        </div>
        <nav
          v-if="hasLegalLinks"
          class="auth-layout__legal"
          :aria-label="AUTH.LEGAL_NAV_LABEL"
          data-testid="auth-layout__legal"
        >
          <a
            v-if="termsHref"
            class="auth-layout__legal-link"
            :href="termsHref"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="auth-layout__legal-terms"
          >{{ AUTH.TERMS_LABEL }}</a>
          <span
            v-if="termsHref && privacyHref"
            class="auth-layout__legal-sep"
            aria-hidden="true"
          >{{ AUTH.LEGAL_SEPARATOR }}</span>
          <a
            v-if="privacyHref"
            class="auth-layout__legal-link"
            :href="privacyHref"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="auth-layout__legal-privacy"
          >{{ AUTH.PRIVACY_LABEL }}</a>
        </nav>
      </div>
    </v-app>
  </div>
</template>

<script>
import constants from '../../../plugins/constants';
import AppLoadingScreen from '../../common/AppLoadingScreen.vue';
import AuthBrandPanel from './AuthBrandPanel.vue';
import {
  AUTH_BRAND_PANEL_IFRAME_SANDBOX,
  LOGIN_SCREENS_IFRAME_TIMEOUT_MS,
  fetchLoginBrandSlides,
  waitWithTimeout,
} from './AuthBrandPanelUtils';

const { AUTH } = constants;

export default {
  name: 'AuthLayout',
  components: {
    AppLoadingScreen,
    AuthBrandPanel,
  },
  props: {
    dataTestid: { type: String, default: 'auth-layout' },
    loading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    waitForBrandPanel: { type: Boolean, default: true },
  },
  data() {
    return {
      brandReady: !this.waitForBrandPanel,
      slides: [],
      preloadSrc: '',
      termsHref: '',
      privacyHref: '',
      AUTH,
      AUTH_BRAND_PANEL_IFRAME_SANDBOX,
    };
  },
  computed: {
    layoutVisible() {
      return !this.waitForBrandPanel || this.brandReady;
    },
    hasLegalLinks() {
      return Boolean(this.termsHref || this.privacyHref);
    },
    showLoadingScreen() {
      return this.waitForBrandPanel && !this.brandReady && this.$store.state.loading < 1;
    },
  },
  watch: {
    brandReady: {
      immediate: true,
      handler(ready) {
        if (ready) this.$emit('ready');
      },
    },
  },
  created() {
    this.prepareBrandPanel();
  },
  beforeDestroy() {
    if (this._preloadResolve) this._preloadResolve();
    this._preloadResolve = null;
  },
  methods: {
    onPreloadIframeReady() {
      if (this._preloadResolve) this._preloadResolve();
    },
    async prepareBrandPanel() {
      const payload = await fetchLoginBrandSlides(this.axios);
      this.termsHref = payload.termsHref;
      this.privacyHref = payload.privacyHref;
      this.slides = payload.slides;
      if (!this.waitForBrandPanel) {
        this.brandReady = true;
        return;
      }
      const firstSrc = this.slides[0] && this.slides[0].iframeSrc;
      if (!firstSrc) {
        this.brandReady = true;
        return;
      }
      this.preloadSrc = firstSrc;
      const loaded = new Promise((resolve) => {
        this._preloadResolve = resolve;
      });
      await waitWithTimeout(loaded, LOGIN_SCREENS_IFRAME_TIMEOUT_MS);
      if (this._isDestroyed) return;
      this.preloadSrc = '';
      this._preloadResolve = null;
      this.brandReady = true;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './AuthLayout.scss';
</style>
